import { View, Text, StyleSheet, Pressable } from 'react-native';
import React, { useEffect, useMemo, useState } from 'react';
import Constants from '../../constants';
import Radio from '../Radio';
import {
  AsesInstrumentConfig,
  AsesQuestion,
  AsesSection,
} from '../../utils/proInstrument';
import {
  OxfordAnswers,
  buildOxfordLeadValues,
  canAdvanceFromOxfordQuestion,
  parseOxfordAnswers,
} from '../../utils/oxfordSubmit';

type StepItem = {
  section: AsesSection;
  question: AsesQuestion;
};

/** Shared 12-question Oxford instrument wizard (OKS knee + OHS hip). */
export default function OxfordForm({
  instrumentConfig,
  color = Constants.colors.blue,
  values,
  onChange,
}: {
  instrumentConfig: AsesInstrumentConfig;
  color?: string;
  values?: string[];
  onChange: (values: string[]) => void;
}): JSX.Element {
  const [answers, setAnswers] = useState<OxfordAnswers>(() => parseOxfordAnswers(values));
  const [stepIndex, setStepIndex] = useState(0);

  const steps = useMemo(() => {
    const items: StepItem[] = [];
    for (const section of instrumentConfig.sections) {
      for (const question of section.questions) {
        items.push({ section, question });
      }
    }
    return items;
  }, [instrumentConfig]);

  const currentStep = steps[stepIndex];
  const previousStep = stepIndex > 0 ? steps[stepIndex - 1] : undefined;
  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex === steps.length - 1;
  const showSectionTitle = !!currentStep && currentStep.section.id !== previousStep?.section.id;

  useEffect(() => {
    setAnswers(parseOxfordAnswers(values));
  }, [values?.join('|')]);

  const updateAnswer = (questionId: string, value: string | number) => {
    const next = { ...answers, [questionId]: value };
    setAnswers(next);
    onChange(buildOxfordLeadValues(next));
  };

  const canAdvance = currentStep ? canAdvanceFromOxfordQuestion(currentStep.question, answers) : false;

  const goBack = () => {
    if (!isFirstStep) {
      setStepIndex(stepIndex - 1);
    }
  };

  const goNext = () => {
    if (!canAdvance || isLastStep) {
      return;
    }
    setStepIndex(stepIndex + 1);
  };

  const renderQuestionInput = (question: AsesQuestion) => {
    const current = answers[question.id];
    return (
      <View>
        {question.options?.map((option) => (
          <Radio
            key={`${question.id}-${option.value}`}
            color={color}
            active={Number(current) === option.value}
            label={option.label}
            onPress={() => updateAnswer(question.id, option.value)}
          />
        ))}
      </View>
    );
  };

  if (!currentStep) {
    return <View />;
  }

  const { section, question } = currentStep;

  return (
    <View style={styles.form}>
      <View style={styles.progressRow}>
        <Text style={styles.progressText}>
          Question {stepIndex + 1} of {steps.length}
        </Text>
        <View style={styles.progressTrack}>
          <View
            style={[
              styles.progressFill,
              {
                backgroundColor: color,
                width: `${((stepIndex + 1) / steps.length) * 100}%`,
              },
            ]}
          />
        </View>
      </View>

      {showSectionTitle ? (
        <Text style={[styles.sectionTitle, { color }]}>{section.title}</Text>
      ) : null}

      <View style={styles.question}>
        <Text style={styles.questionText}>{question.text} *</Text>
        {renderQuestionInput(question)}
        {!canAdvance ? (
          <Text style={styles.validationHint}>Please answer before continuing.</Text>
        ) : null}
      </View>

      <View style={styles.navRow}>
        <Pressable
          onPress={goBack}
          disabled={isFirstStep}
          style={[styles.navButton, styles.backButton, isFirstStep && styles.navButtonDisabled]}
        >
          <Text style={[styles.navButtonText, isFirstStep && styles.navButtonTextDisabled]}>Back</Text>
        </Pressable>

        {isLastStep ? (
          <Text style={styles.finishHint}>Press Submit below when ready.</Text>
        ) : (
          <Pressable
            onPress={goNext}
            disabled={!canAdvance}
            style={[
              styles.navButton,
              styles.nextButton,
              { backgroundColor: canAdvance ? color : Constants.colors.info + '55' },
            ]}
          >
            <Text style={styles.nextButtonText}>Next</Text>
          </Pressable>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  form: {
    width: '100%',
  },
  progressRow: {
    marginBottom: 16,
  },
  progressText: {
    fontFamily: 'Gotham',
    fontSize: 13,
    color: Constants.colors.info,
    marginBottom: 8,
  },
  progressTrack: {
    height: 6,
    borderRadius: 3,
    backgroundColor: Constants.colors.info + '22',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 3,
  },
  sectionTitle: {
    fontFamily: 'GothamBold',
    fontSize: 16,
    marginBottom: 12,
  },
  question: {
    marginBottom: 20,
    minHeight: 120,
  },
  questionText: {
    fontFamily: 'Gotham',
    fontSize: 15,
    marginBottom: 12,
    lineHeight: 22,
  },
  validationHint: {
    fontFamily: 'Gotham',
    fontSize: 12,
    color: Constants.colors.red,
    marginTop: 10,
  },
  navRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  navButton: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 20,
    minWidth: 90,
    alignItems: 'center',
  },
  backButton: {
    backgroundColor: Constants.colors.info + '22',
  },
  nextButton: {},
  navButtonDisabled: {
    opacity: 0.4,
  },
  navButtonText: {
    fontFamily: 'GothamBold',
    color: Constants.colors.blue,
  },
  navButtonTextDisabled: {
    color: Constants.colors.info,
  },
  nextButtonText: {
    fontFamily: 'GothamBold',
    color: '#FFF',
  },
  finishHint: {
    flex: 1,
    fontFamily: 'Gotham',
    fontSize: 13,
    color: Constants.colors.info,
    textAlign: 'right',
  },
});
